import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { RoomTypeService } from '../room-type.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { IRoomType } from '../iroomtype';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { LookupService } from 'src/app/services/lookup.service';
import { catchError, debounceTime, finalize, map, Observable, of, startWith, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { BillChargeDetailComponent } from '../../../hospital-setting/bill-charge/bill-charge-detail/bill-charge-detail.component';
import { WardDetailComponent } from '../../ward/ward-detail/ward-detail.component';
import { ValidationService } from 'src/app/common/validation/validation.service';
import { CapitalizeFirstLetterDirective } from 'src/app/common/directive/capitalize-first-letter.directive';

import { HasRoleDirective } from 'src/app/common/directive/has-role.directive';
import { ROLE_GROUPS } from 'src/app/common/authorization/roles';

@Component({
  selector: 'app-room-type-detail',
  standalone: true,
  imports: [
    HasRoleDirective,
    CommonModule,
    MaterialModule,
    TranslateModule,
    ReactiveFormsModule,
    TablerIconsModule,
    CapitalizeFirstLetterDirective
  ],
  templateUrl: './room-type-detail.component.html',
  styleUrl: './room-type-detail.component.scss'
})
export class RoomTypeDetailComponent implements OnInit, OnDestroy {
  readonly ROLE_GROUPS = ROLE_GROUPS;
  roomTypeForm!: FormGroup;
  isEdit: boolean = false;
  billchargeslist: CommonDropDown[] = [];
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  wardddl: CommonDropDown[] = [];
  filteredward$: Observable<CommonDropDown[]>;
  wardCtrl = new FormControl();
  showAddOptionforWard = false;

  patchRoomTypeData: IRoomType;

  constructor(
    private _roomtypeService: RoomTypeService,
    private _formbuilder: FormBuilder,
    private dialogRef: MatDialogRef<RoomTypeDetailComponent>,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    @Inject(MAT_DIALOG_DATA) private _data: { roomtype: IRoomType, isEdit: boolean, wardId?: number },
    private _lookupService: LookupService,
    private dialog: MatDialog,
  ) {
    this.isEdit = _data?.isEdit;
    this.patchRoomTypeData = _data?.roomtype;
  }

  ngOnInit(): void {
    // Initialize form first
    const initialWardId = this.isEdit ? this.patchRoomTypeData?.wardId : (this._data?.wardId || '');
    this.roomTypeForm = this._formbuilder.group({
      name: [this.patchRoomTypeData?.name || '', [Validators.required, Validators.maxLength(50)]],
      wardId: [initialWardId, [Validators.required, Validators.maxLength(20)]],
      maxBeds: [this.patchRoomTypeData?.maxBeds || '', [Validators.required, Validators.min(1), Validators.max(100), ValidationService.numericValidator]],
      chargeId: [this.patchRoomTypeData?.chargeId || '', [Validators.required]],
      isActive: [this.patchRoomTypeData?.isActive ?? true]
    });

    if (initialWardId) {
      this.roomTypeForm.get('wardId')?.disable();
    } else {
      this.roomTypeForm.get('wardId')?.enable();
    }

    this._lookupService.getWards()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.wardddl = data || [];
        this.filteredward$ = this.wardCtrl.valueChanges.pipe(
          startWith(''),
          debounceTime(300),
          switchMap(value => {
            const search = typeof value === 'string' ? value : value?.name || '';
            return this._lookupService.getWards(search).pipe(
              tap(list => {
                this.showAddOptionforWard =
                  !!search && !list.some(x => x.name.toLowerCase() === search.toLowerCase());
              }),
              map(list => list)
            );
          })
        );

        // Pre-select ward if editing or if wardId is provided
        if (this.isEdit && this._data?.roomtype?.wardId) {
          const selected = this.wardddl.find(x => x.id === this._data.roomtype.wardId);
          if (selected) {
            this.wardCtrl.setValue(selected);
            this.roomTypeForm.patchValue({ wardId: selected.id });
          }
        } else if (!this.isEdit && this._data?.wardId) {
          // Pre-select ward when adding new room type with a specific ward
          const selected = this.wardddl.find(x => x.id === this._data.wardId);
          if (selected) {
            this.wardCtrl.setValue(selected);
            this.roomTypeForm.patchValue({ wardId: selected.id });
          }
        }
      });

    this._lookupService.billcharges$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((charge: CommonDropDown[]) => {
        this.billchargeslist = charge;
      });
  }

  onAddRoomType() {
    if (this.roomTypeForm.invalid) {
      return;
    }

    const data = this.roomTypeForm.getRawValue();
    this._roomtypeService.createRoomType(data)
      .pipe(
        takeUntil(this._unsubscribeAll),
        catchError((error) => {
          this.toastr.error(this.translate.instant('COMMON.ERROR'));
          console.error('Error creating room type:', error);
          return of(null);
        })
      )
      .subscribe({
        next: (response) => {
          if (response?.isSuccess) {
            this.dialogRef.close(response);
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.ROOM_TYPE')));
            this.wardLookup();
          } else if (response) {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          }
        }
      });
  }

  onUpdateRoomType() {
    if (this.roomTypeForm.invalid) {
      return;
    }

    const data = {
      id: this._data?.roomtype?.id,
      ...this.roomTypeForm.getRawValue()
    };

    this._roomtypeService.updateRoomType(data)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.dialogRef.close(response);
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.ROOM_TYPE')));
          }
        },
        error: (error) => {
          if (error?.error?.errors) {
            const allErrors = Object.values(error.error.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          } else {
            this.toastr.error('Something went wrong');
          }
        }
      });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  addBillCharge() {
    this.dialog.open(BillChargeDetailComponent, {
      maxWidth: '70vh',
      width: '90%',
    }).afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((response) => {
        if (response?.result) {
          this.billChargeLookup();
          this.roomTypeForm.get('chargeId')?.setValue(response.result.id);
        }
      });
  }

  private billChargeLookup() {
    this._lookupService.getBillCharge().pipe(take(1)).subscribe();
  }

  private wardLookup() {
    this._lookupService.getWards().pipe(take(1)).subscribe();
  }

  displayward(ward: CommonDropDown): string {
    return ward && ward.name ? ward.name : '';
  }

  onWardSelected(ward: CommonDropDown) {
    this.roomTypeForm.patchValue({
      wardId: ward.id
    });
    this.wardCtrl.setValue(ward);
  }

  addward(name: string) {
    const dialogRef = this.dialog.open(WardDetailComponent, {
      width: '70vw',
      data: {
        ward: { name: name },
        isEdit: false
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((response) => {
        if (!response?.result) return;
        // refresh lookup
        this._lookupService.getWards()
          .pipe(take(1), takeUntil(this._unsubscribeAll))
          .subscribe(list => {
            this.wardddl = list;
            const newWard = list.find(x => x.id === response.result.id);
            // set in dropdown
            this.wardCtrl.setValue(newWard);
            // set wardId in form
            this.roomTypeForm.patchValue({
              wardId: newWard?.id
            });
          });
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

}
